"use client";

import { c as _c } from "react/compiler-runtime";
import { Box, type BoxProps, Stack } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { ScrollArea } from "@/shared/ui";
import { AccountNavigation } from "@/layouts/app-shell/navigation/account-navigation/account-navigation";
import { NavigationLogo } from "@/layouts/app-shell/navigation/navigation-logo/navigation-logo";
import { NavigationMenu } from "@/layouts/app-shell/navigation/navigation-menu/navigation-menu";
import { useSidebarNavGroups } from "@/layouts/app-shell/navigation/use-sidebar-nav-groups";
import { useSidebarLayout } from "@/layouts/app-shell/sidebar-layout";
import { useOutsideClose } from "@/hooks/use-outside-close";
import { useIsTouch } from "@/hooks/use-touch";
import { cn } from "@/lib/utils";
import { type SidebarConfig } from "@/types/sidebar";
import classes from "./desktop-navigation.module.scss";
type DesktopNavigationProps = BoxProps & {
  config?: SidebarConfig;
};
export function DesktopNavigation(t0) {
  const $ = _c(52);
  let className;
  let config;
  let props;
  if ($[0] !== t0) {
    ({
      config,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = config;
    $[3] = props;
  } else {
    className = $[1];
    config = $[2];
    props = $[3];
  }
  const {
    isMobile,
    open,
    setOpen,
    showCollapsedVisual,
    phase
  } = useSidebarLayout();
  const isTouch = useIsTouch();
  const isTablet = isTouch && !isMobile;
  const collapseTimer = useRef(null);
  const innerRef = useRef(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const {
    sidebarConfig,
    groups,
    profile,
    isProfileLoading
  } = useSidebarNavGroups(config);
  let t1;
  if ($[4] !== setOpen) {
    t1 = () => setOpen(false);
    $[4] = setOpen;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useOutsideClose(innerRef, open && !isMobile, t1);
  let t2;
  if ($[6] !== isMobile || $[7] !== isTablet || $[8] !== panelOpen || $[9] !== phase || $[10] !== setOpen) {
    t2 = () => {
      if (isMobile || isTablet || panelOpen || phase === "menu-out") {
        return;
      }
      if (collapseTimer.current) {
        clearTimeout(collapseTimer.current);
      }
      collapseTimer.current = setTimeout(() => setOpen(true), 80);
    };
    $[6] = isMobile;
    $[7] = isTablet;
    $[8] = panelOpen;
    $[9] = phase;
    $[10] = setOpen;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const handleMouseEnter = t2;
  let t3;
  if ($[12] !== isMobile || $[13] !== isTablet || $[14] !== panelOpen || $[15] !== setOpen) {
    t3 = () => {
      if (isMobile || isTablet || panelOpen) {
        return;
      }
      if (collapseTimer.current) {
        clearTimeout(collapseTimer.current);
      }
      collapseTimer.current = setTimeout(() => setOpen(false), 250);
    };
    $[12] = isMobile;
    $[13] = isTablet;
    $[14] = panelOpen;
    $[15] = setOpen;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const handleMouseLeave = t3;
  let t4;
  let t5;
  if ($[17] !== panelOpen || $[18] !== setOpen) {
    t4 = () => {
      if (!panelOpen) {
        return;
      }
      if (collapseTimer.current) {
        clearTimeout(collapseTimer.current);
        collapseTimer.current = null;
      }
      setOpen(false);
    };
    t5 = [panelOpen, setOpen];
    $[17] = panelOpen;
    $[18] = setOpen;
    $[19] = t4;
    $[20] = t5;
  } else {
    t4 = $[19];
    t5 = $[20];
  }
  useEffect(t4, t5);
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = cn(classes.root, "group");
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const t7 = open ? "expanded" : "collapsed";
  const t8 = showCollapsedVisual ? "icon" : "";
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Box data-slot="sidebar-gap" className={classes.gap} aria-hidden={true} />;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== className) {
    t10 = cn(classes.container, className);
    $[23] = className;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== sidebarConfig.streamerHubInfo) {
    t11 = sidebarConfig.streamerHubInfo && <Box component="header" className={classes.header} pt="sm" pb={0}><NavigationLogo logo={sidebarConfig.streamerHubInfo.logo} /></Box>;
    $[25] = sidebarConfig.streamerHubInfo;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== groups || $[28] !== isTablet) {
    t12 = groups.length > 0 && <NavigationMenu groups={groups} showToggle={isTablet} />;
    $[27] = groups;
    $[28] = isTablet;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t12) {
    t13 = <Box component="section" className={classes.content}><ScrollArea className={classes.contentScroll}>{t12}</ScrollArea></Box>;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== isProfileLoading || $[33] !== panelOpen || $[34] !== profile) {
    t14 = profile && <AccountNavigation user={profile} isLoading={isProfileLoading} panelOpen={panelOpen} onPanelOpenChange={setPanelOpen} />;
    $[32] = isProfileLoading;
    $[33] = panelOpen;
    $[34] = profile;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t14) {
    t15 = <Box component="footer" className={classes.footer}>{t14}</Box>;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== handleMouseEnter || $[39] !== handleMouseLeave || $[40] !== t11 || $[41] !== t13 || $[42] !== t15) {
    t16 = <Stack ref={innerRef} className={classes.inner} gap={0} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>{t11}{t13}{t15}</Stack>;
    $[38] = handleMouseEnter;
    $[39] = handleMouseLeave;
    $[40] = t11;
    $[41] = t13;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== props || $[45] !== t10 || $[46] !== t16) {
    t17 = <Box component="nav" data-slot="sidebar-container" className={t10} {...props}>{t16}</Box>;
    $[44] = props;
    $[45] = t10;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t17 || $[49] !== t7 || $[50] !== t8) {
    t18 = <Box className={t6} data-slot="sidebar" data-state={t7} data-collapsible={t8}>{t9}{t17}</Box>;
    $[48] = t17;
    $[49] = t7;
    $[50] = t8;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  return t18;
}